import { toast } from 'sonner';
import i18next from 'i18next';

import { useWorkflowTemplateStore } from '@/store/workflow-template.store.js';
import { useTenantStore } from '@/store/tenant.store';
import { mapColumnFiltersForQuery } from '@/lib/table-filter-query.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class WorkflowTemplateIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    _err(error) {
        if (error?.code === 'DUPLICATE_TEMPLATE_CODE') {
            const d = error.details && typeof error.details === 'object' ? error.details : {};
            return i18next.t('workflow:messages.duplicateTemplateCode', {
                defaultValue:
                    'The code "{{code}}" (v{{version}}) already exists for this entity. Enter a different code or version from the Meta tab.',
                code: d.code ?? '?',
                version: d.version ?? 1,
            });
        }
        return getIntegrationErrorMessage(error);
    }

    async fetchById(id) {
        try {
            const response = await this.service.getById(id);
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._err(error));
            return { success: false, error: this._err(error) };
        }
    }

    async fetchTemplates(params = {}) {
        const store = useWorkflowTemplateStore.getState();
        store.setLoading(true);
        store.clearError();
        try {
            const { columnFilters, ...requestParams } = params;
            const response = await this.service.getAll({
                ...requestParams,
                ...mapColumnFiltersForQuery(columnFilters),
            });
            const items = (response.data || []).map((it) => ({ ...it, id: it._id || it.id }));
            store.setItems(items);
            if (response.pagination && store.setPaginationData) {
                store.setPaginationData(response.pagination);
            }
            store.setUniqueValues(response.uniqueValues || {});
            return { success: true, data: items, uniqueValues: response.uniqueValues || {} };
        } catch (error) {
            const msg = this._err(error);
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async create(payload) {
        const store = useWorkflowTemplateStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;
        store.setLoading(true);
        try {
            const response = await this.service.create({ ...payload, tenant: payload?.tenant ?? tenantId });
            toast.success(
                i18next.t('workflow:messages.createSuccess', { defaultValue: 'Workflow template created' }),
            );
            store.closeForm?.();
            await this.fetchTemplates();
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async update(id, payload) {
        const store = useWorkflowTemplateStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.update(id, payload);
            toast.success(
                i18next.t('workflow:messages.updateSuccess', { defaultValue: 'Workflow template updated' }),
            );
            store.closeForm?.();
            await this.fetchTemplates();
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async validate(payload) {
        try {
            const response = await this.service.validate(payload);
            return { success: true, valid: response.valid !== false, details: response.details };
        } catch (error) {
            const raw = error?.details;
            const details = Array.isArray(raw) ? raw : raw?.details;
            if (Array.isArray(details)) {
                return { success: true, valid: false, details };
            }
            toast.error(this._err(error));
            return { success: false, error: this._err(error), valid: false, details: [] };
        }
    }

    async fetchPayloadShape(entityType, opts = {}) {
        try {
            const params = { entityType };
            if (opts.strictContract) params.strict = 'true';
            const response = await this.service.getPayloadShape(params);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async publish(id) {
        try {
            const response = await this.service.publish(id);
            toast.success(
                i18next.t('workflow:messages.publishSuccess', { defaultValue: 'Template published' }),
            );
            await this.fetchTemplates();
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._err(error));
            return { success: false, error: this._err(error) };
        }
    }

    /** Re-activate a superseded published template (isPublished && !isActive). */
    async republish(id) {
        try {
            const response = await this.service.publish(id);
            toast.success(
                i18next.t('workflow:messages.republishSuccess', { defaultValue: 'Template republished' }),
            );
            await this.fetchTemplates();
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._err(error));
            return { success: false, error: this._err(error) };
        }
    }

    async cloneToNewVersion(id, payload) {
        try {
            const response = await this.service.cloneToNewVersion(id, payload);
            toast.success(
                i18next.t('workflow:messages.cloneSuccess', { defaultValue: 'New version created' }),
            );
            await this.fetchTemplates();
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._err(error));
            return { success: false, error: this._err(error) };
        }
    }

    /**
     * Peek active published template for an entity type (does not replace list store state).
     */
    async findActivePublishedForEntityType(entityType) {
        if (!entityType?.trim()) return { success: true, data: null };
        try {
            const response = await this.service.getAll({
                entityType: entityType.trim(),
                isPublished: true,
            });
            const rows = response.data || [];
            const active = rows.find((r) => r.isPublished && r.isActive === true);
            return { success: true, data: active || null };
        } catch (error) {
            return { success: false, error: this._err(error), data: null };
        }
    }

    /**
     * List published templates for a given entity type WITHOUT mutating the list store.
     * Used by pickers (e.g. ticket template "default workflow" selector).
     */
    async listPublished(entityType) {
        if (!entityType?.trim()) return { success: true, data: [] };
        try {
            const response = await this.service.getAll({
                entityType: entityType.trim(),
                isPublished: true,
            });
            const rows = (response.data || [])
                .filter((r) => r.isPublished)
                .map((it) => ({ ...it, id: it._id || it.id }));
            return { success: true, data: rows };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg, data: [] };
        }
    }

    async delete(id) {
        try {
            await this.service.delete(id);
            toast.success(i18next.t('workflow:messages.deleteSuccess', { defaultValue: 'Template deleted' }));
            await this.fetchTemplates();
            return { success: true };
        } catch (error) {
            toast.error(this._err(error));
            return { success: false, error: this._err(error) };
        }
    }
}
